import React, {
  memo,
  RefObject,
  useMemo,
} from 'react';
import clsx from 'clsx';
import {
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import ServicesLookup from 'views/EMR/components/ServiceLookup';
import {
  filterCodes,
  normalizeAssessmentCodeOption,
  visitNoteFieldDirtyClasses,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import {
  assessmentLookupClasses,
  visitNoteBillingActions,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/constants';
import type {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import type {
  QueryObserverResult,
} from '@tanstack/react-query';
import type {
  FilterOptionsState,
} from '@mui/material/useAutocomplete';

type Props = {
  patientId: number;
  visitNoteId: number;
  sectionRef?: RefObject<HTMLDivElement>;
  favorites?: VisitNoteServiceCode[];
  refetchFavoriteCode?: () => Promise<QueryObserverResult>;
  saveVisitNote?: () => Promise<void>;
};

type SectionProps = Props & {
  field: FieldValues & { onChange: (value?: VisitNoteServiceCode[]) => void };
  isDirty: boolean;
};

function AssessmentCodesSectionContent({
  field,
  isDirty,
  sectionRef,
  ...props
}: SectionProps) {
  const classes = useMemo(() => ({
    ...assessmentLookupClasses,
    container: clsx({ [visitNoteFieldDirtyClasses]: isDirty }),
  }), [isDirty]);

  return (
    <ServicesLookup
      {...field}
      type="ICDCode"
      label="Assessment (ICD-10)"
      dropdownId="icd-code-selection"
      id="assessmentCodes"
      containerRef={sectionRef}
      saveVisitNote={props.saveVisitNote}
      visitNoteId={props.visitNoteId}
      externalFilter={filterCodes as (
        options: VisitNoteServiceCode[],
        state: FilterOptionsState<VisitNoteServiceCode>,
      ) => VisitNoteServiceCode[]}
      patientId={props.patientId}
      favorites={props.favorites ?? []}
      refetchFavoriteCode={props.refetchFavoriteCode}
      classes={classes}
      actions={visitNoteBillingActions}
      normalizeOption={normalizeAssessmentCodeOption}
    />
  );
}

const AssessmentCodesSectionContentMemoized = memo(
  AssessmentCodesSectionContent,
);

function AssessmentCodesSection(props: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name="bill.assessmentCodes"
      control={control}
      render={({ field, fieldState: { isDirty } }) => (
        <AssessmentCodesSectionContentMemoized
          {...props}
          field={field}
          isDirty={isDirty}
        />
      )}
    />
  );
}

export default memo(AssessmentCodesSection);
